/**
 * Dispatch-coordination capacity profiles for the Ontario Corps illustrative
 * layer (V.3 §6). These are ILLUSTRATIVE figures sized to each partner's
 * real-world footprint — they are NOT a live feed. The DispatchCapacityPanel
 * banners them as pending a data partnership with MEPR.
 *
 * Profiles are keyed by partner slug (the OC external_id base). A marker's
 * external_id is `<slug>-<i>` and the partner org row's is `<slug>`, so both
 * resolve to the same profile via `partnerSlug()`.
 */

export interface EquipmentItem {
  /** Lucide glyph name (rendered through iconRegistry). */
  icon: string;
  label: string;
  count: string;
}

export interface CapacityProfile {
  /** Personnel ready within [24h, 48h, 72h]. */
  readiness: [number, number, number];
  equipment: EquipmentItem[];
  /** Capability headlines for "Best deployed for". */
  bestFor: string[];
}

/** Strip the trailing `-<n>` marker suffix to get the partner slug. */
export function partnerSlug(externalId: string | null): string | null {
  if (!externalId) return null;
  return externalId.replace(/-\d+$/, '');
}

/**
 * Illustrative capacity per partner. Numbers are order-of-magnitude estimates
 * of each organisation's Ontario surge capacity — plausible, not authoritative.
 */
export const CAPACITY_BY_SLUG: Record<string, CapacityProfile> = {
  // --- Search & Rescue / Disaster Response ---
  osarva: {
    readiness: [30, 80, 150],
    equipment: [
      { icon: 'dog', label: 'K9 search teams', count: '12' },
      { icon: 'users', label: 'Ground/marine teams', count: '40+' },
      { icon: 'anchor', label: 'Boats', count: '15' },
    ],
    bestFor: ['Wilderness & ground search', 'Swiftwater / shoreline', 'Lost-person K9'],
  },
  'team-rubicon': {
    readiness: [50, 150, 300],
    equipment: [
      { icon: 'axe', label: 'Sawyer (chainsaw) crews', count: '12' },
      { icon: 'truck', label: 'Mobile ops trailers', count: '4' },
      { icon: 'tree-pine', label: 'Hazard-tree teams', count: '8' },
    ],
    bestFor: ['Firebreak cutting', 'Hazard-tree removal', 'Debris clearance'],
  },
  // --- Medical Response ---
  globalmedic: {
    readiness: [20, 60, 120],
    equipment: [
      { icon: 'plane', label: 'RescUAV drone units', count: '6' },
      { icon: 'droplets', label: 'Water-purification units', count: '20' },
      { icon: 'package', label: 'Family emergency kits', count: '5,000' },
    ],
    bestFor: ['Aerial damage assessment', 'Safe drinking water', 'Relief kit distribution'],
  },
  'st-john-ambulance': {
    readiness: [500, 1500, 3000],
    equipment: [
      { icon: 'heart-pulse', label: 'First-aid teams', count: '120' },
      { icon: 'ambulance', label: 'Response units', count: '40' },
      { icon: 'dog', label: 'Therapy dog teams', count: '60' },
    ],
    bestFor: ['Shelter first aid', 'Medical standby', 'Psychological first aid'],
  },
  'attch-niagara': {
    readiness: [10, 30, 60],
    equipment: [
      { icon: 'heart-pulse', label: 'Trauma clinicians', count: '15' },
      { icon: 'users', label: 'Responder-support teams', count: '6' },
    ],
    bestFor: ['Responder mental health', 'Trauma therapy', 'Critical-incident support'],
  },
  // --- Shelter & Welfare ---
  'salvation-army': {
    readiness: [3000, 8000, 15000],
    equipment: [
      { icon: 'truck', label: 'Mobile feeding canteens', count: '60' },
      { icon: 'bed', label: 'Bunkhouse trailers', count: '25' },
      { icon: 'utensils', label: 'Meals/day surge', count: '50,000' },
    ],
    bestFor: ['Mass feeding & hydration', 'Emergency lodging', 'Evacuee reception'],
  },
  'habitat-humanity': {
    readiness: [40, 120, 250],
    equipment: [
      { icon: 'hard-hat', label: 'Rebuild crews', count: '20' },
      { icon: 'wrench', label: 'Tool/equipment sets', count: '60' },
    ],
    bestFor: ['Post-disaster repair', 'Rebuild labour', 'Damage triage'],
  },
  'feed-ontario': {
    readiness: [60, 180, 400],
    equipment: [
      { icon: 'warehouse', label: 'Member food banks', count: '1,200' },
      { icon: 'box', label: 'Pallets staged', count: '8,000' },
    ],
    bestFor: ['Food/supply distribution', 'Cold-chain logistics', 'Evacuee provisioning'],
  },
  // --- Heavy & Specialty Rescue ---
  'mine-rescue': {
    readiness: [15, 40, 80],
    equipment: [
      { icon: 'shield-alert', label: 'SCBA rescue sets', count: '40' },
      { icon: 'flame', label: 'Thermal-imaging cameras', count: '18' },
      { icon: 'wrench', label: 'Confined-space rigs', count: '9' },
    ],
    bestFor: ['Structural collapse', 'Confined-space rescue', 'Rope / high-angle'],
  },
  orba: {
    readiness: [100, 300, 600],
    equipment: [
      { icon: 'truck', label: 'Graders / dozers', count: '120' },
      { icon: 'container', label: 'Excavators', count: '80' },
      { icon: 'hard-hat', label: 'Operator crews', count: '300' },
    ],
    bestFor: ['Firebreak construction', 'Road access / clearing', 'Heavy debris'],
  },
  // --- Supplies & Logistics ---
  'home-depot': {
    readiness: [50, 150, 350],
    equipment: [
      { icon: 'zap', label: 'Generators', count: '2,000' },
      { icon: 'package', label: 'Tarps / plywood', count: '40,000' },
      { icon: 'droplet', label: 'Water pallets', count: '6,000' },
    ],
    bestFor: ['Generators & power', 'Building materials', 'Stores as command posts'],
  },
  labatt: {
    readiness: [10, 30, 60],
    equipment: [
      { icon: 'droplets', label: 'Canned drinking water', count: '500,000' },
      { icon: 'truck', label: 'Delivery trucks', count: '30' },
      { icon: 'warehouse', label: 'Refrigerated warehousing', count: '4' },
    ],
    bestFor: ['Emergency drinking water', 'Bulk transport', 'Cold storage'],
  },
  uber: {
    readiness: [200, 600, 1500],
    equipment: [
      { icon: 'truck', label: 'Driver-partners on platform', count: '1,500+' },
      { icon: 'package', label: 'Goods-transport capacity', count: 'on-demand' },
    ],
    bestFor: ['Evacuee transport', 'Last-mile goods', 'Ride vouchers'],
  },
  // --- Indigenous Coordination ---
  ifna: {
    readiness: [20, 60, 120],
    equipment: [
      { icon: 'users', label: 'Community liaisons', count: '30' },
      { icon: 'plane', label: 'Fly-in coordination', count: 'multi-community' },
    ],
    bestFor: ['Fly-in community support', 'Evacuation coordination', 'Local knowledge'],
  },
  'kenora-chiefs': {
    readiness: [15, 45, 90],
    equipment: [
      { icon: 'tent', label: 'Wellness-camp capacity', count: '327 acres' },
      { icon: 'heart-pulse', label: 'Health/social teams', count: '20' },
    ],
    bestFor: ['Evacuee wellness camp', 'Health & social services', 'Treaty 3 relief'],
  },
  ofifc: {
    readiness: [40, 120, 250],
    equipment: [
      { icon: 'home', label: 'Friendship Centres', count: '29' },
      { icon: 'users', label: 'Reception/support staff', count: '120' },
    ],
    bestFor: ['Urban Indigenous reception', 'Evacuee support', 'Cultural services'],
  },
  // --- Communications & Info ---
  ares: {
    readiness: [25, 70, 140],
    equipment: [
      { icon: 'radio', label: 'Operators / stations', count: '70' },
      { icon: 'megaphone', label: 'Repeater coverage', count: 'province-wide' },
    ],
    bestFor: ['Backup emergency comms', 'Infrastructure-down relay', 'Net control'],
  },
  '211-ontario': {
    readiness: [10, 25, 50],
    equipment: [
      { icon: 'radio', label: 'Call-centre operators', count: '50' },
      { icon: 'life-buoy', label: 'Resource database', count: '60,000 services' },
    ],
    bestFor: ['Evacuee information line', 'Resource routing', 'Situational info'],
  },
  // --- Business Coordination ---
  occ: {
    readiness: [20, 60, 120],
    equipment: [
      { icon: 'briefcase', label: 'Local chambers', count: '135' },
      { icon: 'users', label: 'Business network', count: '60,000 members' },
    ],
    bestFor: ['Business-network mobilization', 'Supply sourcing', 'Recovery coordination'],
  },
};

/** Resolve the capacity profile for an OC asset by its external_id. */
export function capacityFor(externalId: string | null): CapacityProfile | null {
  const slug = partnerSlug(externalId);
  return slug ? (CAPACITY_BY_SLUG[slug] ?? null) : null;
}

export type DeploymentStatus = 'AVAILABLE' | 'PARTIALLY DEPLOYED' | 'FULLY DEPLOYED';

/** Map the row status to the dispatch status label. */
export function deploymentStatus(status: string): DeploymentStatus {
  switch (status) {
    case 'partially_deployed':
    case 'partial':
      return 'PARTIALLY DEPLOYED';
    case 'deployed':
    case 'fully_deployed':
      return 'FULLY DEPLOYED';
    default:
      return 'AVAILABLE';
  }
}

/** "Best deployed for" defaults for real (non-illustrative) operational classes. */
export const CLASS_BEST_FOR: Record<string, string[]> = {
  fire_station: ['Structural fire', 'Initial wildfire attack', 'Technical rescue'],
  medical_facility: ['Casualty care', 'Evacuee triage', 'Medical surge'],
  aviation_base: ['Air search & rescue', 'Aerial transport', 'Reconnaissance'],
  marine_vessel: ['Waterborne SAR', 'Shoreline access', 'Marine evacuation'],
  // Base depots & operational classes (R.1.5 panel polish, Task #22).
  wildfire_base: ['Air-tanker reload & dispatch', 'Initial-attack aviation', 'Sustained air support'],
  volunteer_chapter: ['Surge volunteers', 'Shelter & welfare', 'Local mobilization'],
  utility_response: ['Power restoration', 'Line / infrastructure repair', 'Hazard isolation'],
  specialty_team: ['Specialized technical response', 'Task-specific deployment'],
};

/** response_assets.readiness → short operator label (paired with the colour, never
 *  hue-alone). 'unknown' is omitted at the call site rather than shown as noise. */
export const READINESS_LABEL: Record<string, string> = {
  ready: 'Ready',
  partial: 'Partially ready',
  unavailable: 'Unavailable',
  unknown: 'Unknown',
};

/** response_assets.time_to_deploy_band → short operator label. */
export const TIME_TO_DEPLOY_LABEL: Record<string, string> = {
  immediate: 'Deploys immediately',
  '24h': 'Deploys within 24h',
  '48h': 'Deploys within 48h',
  '72h': 'Deploys within 72h',
  on_call: 'On call',
  unknown: 'Deploy time unknown',
};
